import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { refs, type CostItem } from '@/api/refs';
import { useConfirm } from '@/components/ConfirmProvider';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * ч.1364: статті витрат — довідник акаунта для зведення «Фінанси» і витратних документів
 * (авансові звіти, послуги в надходженнях, зарплата, довідки). Типовий набір ставиться сам
 * (зарплата, оренда, комунальні…); без статті витрата у «Фінансах» усе одно потрапляє в
 * категорію за проводкою — довідник потрібен лише тому, хто хоче деталізувати.
 */
export default function CostItemsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const [newName, setNewName] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const list = useQuery({ queryKey: ['cost-items', 'usage'], queryFn: refs.costItemsOwn.listWithUsage });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['cost-items'] });
  };
  const errText = (e: unknown, fb: string) =>
    (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? fb;

  const create = useMutation({
    mutationFn: () => refs.costItemsOwn.create({ name: newName.trim() }),
    onSuccess: () => { setNewName(''); setNote(null); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося створити статтю.')),
  });
  const update = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => refs.costItemsOwn.update(id, { name }),
    onSuccess: () => { setNote(null); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося перейменувати статтю.')),
  });
  const remove = useMutation({
    mutationFn: (id: string) => refs.costItemsOwn.remove(id),
    onSuccess: () => { setNote(null); invalidate(); },
    onError: (e) => setNote(errText(e, 'Не вдалося видалити статтю.')),
  });

  const columns = useMemo<ColumnDef<CostItem>[]>(() => [
    {
      key: 'name', header: 'Назва', sortValue: (r) => r.name.toLowerCase(),
      cell: (r) => (
        <input defaultValue={r.name} aria-label={`Назва: ${r.name}`} style={{ width: '100%' }}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v && v !== r.name) update.mutate({ id: r.id, name: v });
          }} />
      ),
    },
    { key: 'code', header: 'Код', width: 140, sortValue: (r) => r.code,
      cell: (r) => <span style={{ fontFamily: 'monospace' }}>{r.code}</span> },
    { key: 'usage', header: 'Рядків документів', width: 160, align: 'right',
      sortValue: (r) => r.usage_count ?? 0, cell: (r) => r.usage_count ?? 0 },
    {
      key: 'actions', header: '', width: 110, align: 'right',
      cell: (r) => (
        <button type="button" className="btn-link" disabled={!!r.usage_count || remove.isPending}
          title={r.usage_count ? 'Стаття використовується у документах — видалити не можна' : undefined}
          onClick={async () => {
            if (await confirm({ title: 'Видалити статтю?', message: `«${r.name}» зникне зі списку вибору.`, okLabel: 'видалити', danger: true })) {
              remove.mutate(r.id);
            }
          }}>
          видалити
        </button>
      ),
    },
  ], [confirm, remove, update]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Статті витрат</h2>
      </div>
      <p style={{ marginTop: 0, color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
        Статті для витратних документів і зведення «Фінанси». Типовий набір уже є; додайте свої, якщо
        хочете бачити витрати детальніше. Витрата без статті у «Фінансах» усе одно потрапить у зрозумілу
        категорію (зарплата, податки, послуги, амортизація тощо) — за самою проводкою.
      </p>
      <form onSubmit={(e) => { e.preventDefault(); if (newName.trim()) create.mutate(); }}
        style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', marginBottom: '0.75rem' }}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label htmlFor="ci-name">Нова стаття</label>
          <input id="ci-name" value={newName} maxLength={255} placeholder="Наприклад: Програми і підписки"
            onChange={(e) => { setNewName(e.target.value); setNote(null); }} />
        </div>
        <button type="submit" className="primary" disabled={!newName.trim() || create.isPending}>додати</button>
      </form>
      {note && <div className="error" role="alert">{note}</div>}
      <DataTable rows={list.data ?? []} columns={columns} rowKey={(r) => r.id} loading={list.isLoading}
        initialSortKey="name" emptyText="Статей ще немає" />
    </div>
  );
}
